import type { App } from '../app/App';
import { promptBox } from '../ui/components';
import { ApiError } from './ApiClient';
import { language, t } from '../i18n';
import { isKid } from '../game/systems/policy';

/**
 * Make sure we have a guest identity on the server. Prompts for a nickname
 * the first time (kid mode gets a generated one instead); re-registers if the
 * server forgot our token.
 * Returns false when the server is unreachable or the player cancels.
 */
export async function ensureOnline(app: App, askName = true): Promise<boolean> {
  const save = app.store.state;
  if (save.online) {
    app.api.token = save.online.token;
    try {
      let me = await app.api.me();
      if (me.kid !== isKid(save)) me = await app.api.patchMe({ kid: isKid(save), lang: language() });
      if (me.name !== save.online.name) app.store.update(['online'], (s) => s.online && (s.online.name = me.name));
      void goLive(app);
      void app.cloud.start();
      return true;
    } catch (err) {
      if (!(err instanceof ApiError) || (err.status !== 401 && err.status !== 404)) {
        app.toast(err instanceof Error ? err.message : t('Server unavailable'), 'error');
        return false;
      }
      // Token unknown (e.g. server memory store restarted): register again.
    }
  }
  const kid = isKid(save);
  let name = app.store.hero?.name ?? 'Doodler';
  // Kid mode: the server picks a safe name, so there is nothing to type.
  if (askName && !kid) {
    const picked = await promptBox(t('Pick a nickname'), t('Friends will see this name'), save.online?.name ?? name);
    if (picked === null) return false;
    name = picked || name;
  }
  try {
    const res = await app.api.createPlayer(name, kid ? { lang: language() } : undefined);
    app.api.token = res.token;
    app.store.update(['online'], (s) => (s.online = { playerId: res.player.id, name: res.player.name, token: res.token }));
    void goLive(app);
    void app.cloud.start();
    return true;
  } catch (err) {
    app.toast(err instanceof Error ? err.message : t('Server unavailable'), 'error');
    return false;
  }
}

/**
 * Keep the WebSocket open in the background while signed in, so friend
 * requests, presence and battle invites arrive on any screen. Quiet: does
 * nothing when signed out or when the server is unreachable.
 */
export async function goLive(app: App): Promise<void> {
  const online = app.store.state.online;
  if (!online || app.mp.state !== 'offline') return;
  try {
    // A forgotten token would get the socket closed with an error toast;
    // ensureOnline re-registers it the next time an online feature is used.
    app.api.token = online.token;
    await app.api.me();
    await app.mp.connect(online.token);
  } catch {
    /* offline: single-player keeps working; Battle With Friends retries */
  }
}
